// @flow
import { styled } from 'mz-ui-kit'
import { safePseudoelem } from '../../utils'

// Types
import type { BaseStyles } from '../BaseField'

// Constants
const BASE_CLASS = 'input'
const ANIMATION_TIME = '0.3s'
const ANIMATION_CURVE = 'cubic-bezier(0.165, 0.84, 0.44, 1)'
const DEFAULT_ANIMATION = `${ANIMATION_TIME} ${ANIMATION_CURVE}`

// Basic input element styles
const getBasicStyles = (props: BaseStyles) => ({
  opacity: props.$hidden ? 0 : 1,
  transition: `opacity ${DEFAULT_ANIMATION}`,
  display: 'block',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  minHeight: '1.1875em',
  lineHeight: '1.1875em',
  appearance: 'none',
  color: 'currentColor',
  border: 0,
  margin: 0,
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: '7px',
  paddingBottom: '7px',
  minWidth: 0,
  flexGrow: 1,
  boxSizing: 'content-box',
  verticalAlign: 'middle',
  WebkitTapHighlightColor: 'transparent',
  backgroundColor: 'transparent',
  width: '100%',
  ':focus': { outline: 0 },
})

const isMaterialAppearance = appearance =>
  appearance &&
  (appearance.startsWith('material') || appearance.startsWith('hertz'))

// Creates placeholder styles based on give styles props
export const getPlaceholderStyles = ({
  $focus,
  $nonEmpty,
  $haveLabel,
  $alwaysShowPlaceholder,
  $appearance,
  $palette,
}: *) => {
  const active =
    $focus ||
    $nonEmpty ||
    !$haveLabel ||
    $alwaysShowPlaceholder ||
    !isMaterialAppearance($appearance)
  return {
    color: active ? `${$palette('text').lighten(35)}` : 'transparent',
    transition: `color ${DEFAULT_ANIMATION}`,
  }
}

// Styled input that fits well with BaseField
export const HTMLInputElement = styled<BaseStyles>(
  {
    base: 'input',
    mozClassName: `${BASE_CLASS}__inline`,
  },
  props => {
    const phStyles = getPlaceholderStyles(props)
    return {
      ...getBasicStyles(props),
      ...safePseudoelem('::placeholder', phStyles),
      ...safePseudoelem('::-webkit-input-placeholder', phStyles),
      ...safePseudoelem('::-moz-placeholder', phStyles),
      ...safePseudoelem(':-ms-input-placeholder', phStyles),
      ...safePseudoelem('::-ms-clear', { width: 0, height: 0 }),
    }
  }
)

// Styled input that fits well with BaseField
export const HTMLTextareaElement = styled<BaseStyles>(
  {
    base: 'textarea',
    mozClassName: `${BASE_CLASS}__textarea`,
  },
  props => {
    const phStyles = getPlaceholderStyles(props)
    return {
      ...getBasicStyles(props),
      ...safePseudoelem('::placeholder', phStyles),
      ...safePseudoelem('::-webkit-input-placeholder', phStyles),
      ...safePseudoelem('::-moz-placeholder', phStyles),
      ...safePseudoelem(':-ms-input-placeholder', phStyles),
      ...safePseudoelem('::-ms-clear', { width: 0, height: 0 }),
      width: '100%',
      resize: 'none',
    }
  }
)

// HTML's `<select>` field styled just like general input
export const HTMLSelectElement = styled<BaseStyles>(
  {
    base: 'select',
    mozClassName: `${BASE_CLASS}__select`,
  },
  props => {
    const phStyles = props.$nonEmpty ? {} : getPlaceholderStyles(props)
    return {
      ...getBasicStyles(props),
      ...phStyles,
      pointerEvents: props.$loading ? 'none' : 'inherit',
      ...safePseudoelem('::-ms-expand', { display: 'none' }),
    }
  }
)

// Styled div which look exactly like input component, but based on div
export const StaticValueContainer = styled<BaseStyles>(
  {
    base: 'div',
    mozClassName: `${BASE_CLASS}__inline`,
  },
  props => {
    const phStyles = props.$nonEmpty ? {} : getPlaceholderStyles(props)

    // for input with floating label without selected value
    // we show label instead of placeholder
    const showLabelInsteadPlaceholder =
      isMaterialAppearance(props.$appearance) &&
      !props.$alwaysShowPlaceholder &&
      !props.$nonEmpty &&
      !props.$focus

    return {
      ...getBasicStyles(props),
      ...phStyles,
      ...(showLabelInsteadPlaceholder
        ? {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            position: 'relative',
          }
        : {}),
      cursor: props.$disabled ? 'inherit' : 'cursor',
    }
  }
)
